/**
 * Detail sections for the expanded inventory item view: key-facts strip,
 * aligned label/value rows (Product / Logistics / Operations), and status
 * flags.
 *
 * The label/value rows are react-admin <SimpleShowLayout> blocks reading
 * straight from the record context via `source` paths, so the framework
 * renders the labels and empty states. Derived/composite values use
 * <FunctionField>. The key-facts strip and the status-flag chips stay
 * bespoke — react-admin has no equivalent primitive for either.
 */
import { FunctionField, Labeled, SimpleShowLayout, TextField, useRecordContext } from 'react-admin';
import { Box, Chip, Stack, Tooltip, Typography } from '@mui/material';

import { displayNameFor, formatCents, formatDateTime } from './format';
import type { ConstantOption, InventoryItemRecord } from './types';

export const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <Typography
    component="div"
    sx={{
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: '.08em',
      textTransform: 'uppercase',
      color: 'text.secondary',
      mb: 1,
    }}
  >
    {children}
  </Typography>
);

const MONO = "'SFMono-Regular', Consolas, 'Liberation Mono', monospace";

/** Compact spacing for the embedded SimpleShowLayout blocks. */
const sectionSx = {
  p: 0,
  '& .RaSimpleShowLayout-stack': { gap: 0 },
  '& .RaLabeled-label': { fontSize: 11, color: 'text.secondary' },
  '& .RaLabeled-root': { mb: 0.5 },
  '& .MuiTypography-body2': { fontSize: 13 },
};

const monoSx = { fontFamily: MONO, fontSize: 12.5 };

/** Colored status dot + label, for availability-style eligibility values. */
const EligibilityValue = ({ value }: { value: string | null | undefined }) => {
  const color = value === 'ELIGIBLE' ? 'success.main' : value === 'PROVISIONAL' ? 'error.main' : 'warning.main';
  return (
    <Stack direction="row" spacing={0.75} alignItems="center">
      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: value ? color : 'grey.300' }} />
      <Typography variant="body2" sx={{ fontSize: 13 }}>
        {value ?? '—'}
      </Typography>
    </Stack>
  );
};

interface SectionsProps {
  record: InventoryItemRecord;
  states: ConstantOption[];
  conditions: ConstantOption[];
  rejectReasons: ConstantOption[];
}

/** Key-facts strip: the handful of fields a partner scans for first. */
export const KeyFacts = ({ record, conditions }: Pick<SectionsProps, 'record' | 'conditions'>) => {
  const facts: Array<{ label: string, value: React.ReactNode }> = [
    { label: 'Item Price', value: formatCents(record.price) },
    { label: 'Original Price', value: formatCents(record.catalogItem?.originalPrice) },
    { label: 'Condition', value: displayNameFor(conditions, record.condition) },
    { label: 'Display Condition', value: displayNameFor(conditions, record.deployedCondition) },
    { label: 'Location', value: record.location?.code ?? '—' },
    { label: 'Orderable', value: record.canBeOrdered == null ? '—' : record.canBeOrdered ? 'Yes' : 'No' },
  ];
  return (
    <Stack
      direction="row"
      sx={{
        bgcolor: 'grey.50',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        px: 2,
        py: 1.25,
        flexWrap: 'wrap',
        rowGap: 1.5,
      }}
      divider={<Box sx={{ width: '1px', bgcolor: 'divider', mx: 2 }} />}
    >
      {facts.map(({ label, value }) => (
        <Box key={label} sx={{ minWidth: 90 }}>
          <Typography sx={{ fontSize: 10.5, letterSpacing: '.06em', textTransform: 'uppercase', color: 'text.secondary' }}>
            {label}
          </Typography>
          <Typography sx={{ fontSize: 15, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
            {value}
          </Typography>
        </Box>
      ))}
    </Stack>
  );
};

export const ProductSection = () => {
  const record = useRecordContext<InventoryItemRecord>();
  if (!record) return null;
  const catalog = record.catalogItem;
  const facets = catalog?.catalogFacets ?? [];
  const department = facets.find(facet => facet.tag === 'department')?.name;
  const categories = facets.filter(facet => facet.tag === 'category').map(facet => facet.name).join(', ');
  const fabric = catalog?.fabric;
  const measurementFields = catalog?.measurements?.fields ?? [];
  const colorText = catalog?.color ?
    `${catalog.color}${catalog.display_color && catalog.display_color !== catalog.color ? ` (${catalog.display_color})` : ''}` :
    null;
  const seasonText = catalog?.seasonCode ?
    `${catalog.seasonCode}${catalog.season_display ? ` — ${catalog.season_display}` : ''}` :
    null;
  const fabricText = fabric ? `${fabric.code ?? ''}${fabric.name ? ` (${fabric.name})` : ''}` : null;
  const prop65Visible = !!catalog?.prop65_code && catalog.prop65_code !== '0';

  return (
    <Box>
      <SectionLabel>Product</SectionLabel>
      <SimpleShowLayout sx={sectionSx}>
        <TextField source="catalogItem.brand" label="Brand" emptyText="—" />
        <TextField source="catalogItem.sku" label="SKU" sx={monoSx} emptyText="—" />
        <TextField source="catalogItem.parent_sku" label="Parent SKU" sx={monoSx} emptyText="—" />
        <TextField source="catalogItem.partnerSku" label="Brand SKU" sx={monoSx} emptyText="—" />
        <FunctionField label="Color" render={() => colorText ?? '—'} />
        <TextField source="catalogItem.size" label="Size" emptyText="—" />
        {department ? <FunctionField label="Department" render={() => department} /> : null}
        {categories ? <FunctionField label="Category" render={() => categories} /> : null}
        {seasonText ? <FunctionField label="Season" render={() => seasonText} /> : null}
        {fabricText ? <FunctionField label="Fabric" render={() => fabricText} /> : null}
        {fabric?.content ? <FunctionField label="Content" render={() => fabric.content} /> : null}
        {measurementFields.map(({ field, displayName, units }) => {
          const value = record.measurements?.[field];
          if (value === undefined || value === null || value === '') return null;
          return (
            <FunctionField
              key={field}
              label={displayName}
              render={() => `${value}${units === 'in' ? '″' : ` ${units ?? ''}`}`}
            />
          );
        })}
        <TextField source="catalogItem.id" label="Catalog Item ID" sx={monoSx} emptyText="—" />
        {catalog?.upc ? <FunctionField label="UPC" render={() => catalog.upc} sx={monoSx} /> : null}
        {record.troveSerialNumber ? <TextField source="troveSerialNumber" label="Serial Number" sx={monoSx} /> : null}
        {record.resaleUpc?.upc ? <TextField source="resaleUpc.upc" label="Resale UPC" sx={monoSx} /> : null}
        {record.resaleUpc?.parentSku ? <TextField source="resaleUpc.parentSku" label="Resale Parent SKU" sx={monoSx} /> : null}
        {record.preprintedLpn ? <TextField source="preprintedLpn" label="Preprinted LPN" sx={monoSx} /> : null}
        {prop65Visible ? (
          <FunctionField label="Prop65" render={() => catalog?.prop65_code} title={catalog?.prop65_message ?? undefined} />
        ) : null}
        {catalog?.shippingSurchargeAmount ? (
          <FunctionField label="Surcharge" render={() => formatCents(catalog.shippingSurchargeAmount)} />
        ) : null}
      </SimpleShowLayout>
    </Box>
  );
};

export const LogisticsSection = () => {
  const record = useRecordContext<InventoryItemRecord>();
  if (!record) return null;
  const warehouse = record.location?.warehouse;
  const position = record.location?.kind === 'POSITION' ? record.location?.code : record.location?.parentLocationCode;
  const box = record.location?.kind === 'BOX' ? record.location?.code : null;
  const tradein = record.tradeinItems?.[0]?.tradein ?? null;
  const orderText = record.order ?
    `#${record.order.id}${record.order.orderPlacedAt ? ` · placed ${formatDateTime(record.order.orderPlacedAt)}` : ''}` :
    null;

  return (
    <Box>
      <SectionLabel>Logistics</SectionLabel>
      <SimpleShowLayout sx={sectionSx}>
        <FunctionField
          label={warehouse?.purpose ? `Warehouse (${warehouse.purpose})` : 'Warehouse'}
          render={() => warehouse?.code ?? '—'}
        />
        <FunctionField label="Position" render={() => position ?? '—'} />
        <FunctionField label="Box" render={() => box ?? '—'} />
        <FunctionField label="Shipment" render={() => record.shipment?.id ?? record.shipmentId ?? '—'} sx={monoSx} />
        {record.shipment?.source ? <TextField source="shipment.source" label="Shipment Type" /> : null}
        {tradein ? <FunctionField label="Trade-in" render={() => tradein.id} sx={monoSx} /> : null}
        {tradein?.tradeinType ? <FunctionField label="Trade-in Type" render={() => tradein.tradeinType?.toUpperCase()} /> : null}
        {orderText ? <FunctionField label="Order" render={() => orderText} /> : null}
        {record.custodyStatus ? <TextField source="custodyStatus" label="Custody" /> : null}
        {record.sourcePartnerId != null ? <TextField source="sourcePartnerId" label="Source Partner" /> : null}
        {record.wmsStatus?.status ? <TextField source="wmsStatus.status" label="WMS Ingestion" /> : null}
      </SimpleShowLayout>
    </Box>
  );
};

export const OperationsSection = () => {
  const record = useRecordContext<InventoryItemRecord>();
  if (!record) return null;
  const authenticationText = record.authStatus ?
    `${record.authStatus}${record.authMethod ? ` (${record.authMethod})` : ''}` :
    'N/A';
  return (
    <Box>
      <SectionLabel>Operations</SectionLabel>
      <SimpleShowLayout sx={sectionSx}>
        <Labeled label="Storefront">
          <EligibilityValue value={record.catalogItem?.availability} />
        </Labeled>
        <Labeled label="Check-in">
          <EligibilityValue value={record.catalogItem?.availabilityCheckin} />
        </Labeled>
        <Labeled label="Trade-in">
          <EligibilityValue value={record.catalogItem?.mitiAvailability} />
        </Labeled>
        <FunctionField label="Authentication" render={() => authenticationText} />
        <FunctionField label="Repair" render={() => record.repairStatus ?? 'N/A'} />
        <FunctionField label="Cleaning" render={() => record.cleaningStatus ?? 'N/A'} />
        <FunctionField label="Photography" render={() => record.photographyStatus ?? 'N/A'} />
      </SimpleShowLayout>
    </Box>
  );
};

/** Boolean/warning flags rendered as chips — here chips mean genuine flags, not the state labels shown elsewhere. */
export const StatusFlags = ({ record, rejectReasons }: Pick<SectionsProps, 'record' | 'rejectReasons'>) => {
  const flags: Array<{ label: string, color: 'warning' | 'error' | 'default', show: boolean, title?: string }> = [
    {
      label: `Rejected: ${displayNameFor(rejectReasons, record.rejectedReason)}`,
      color: 'error',
      show: !!record.rejectedReason,
    },
    { label: 'Needs Repair', color: 'error', show: !!record.needsRepair },
    { label: 'Needs Cleaning', color: 'warning', show: !!record.needsCleaning },
    { label: 'Needs Photography', color: 'warning', show: !!record.needsPhotography },
    { label: 'Problem Order', color: 'warning', show: !!record.isFlagged },
    { label: 'Keyless', color: 'warning', show: !!record.isKeyless },
    { label: 'Unsellable', color: 'error', show: !!record.isUnsellable },
    { label: 'New With Tags', color: 'default', show: !!record.newWithTags },
    { label: 'Supplier Sendback', color: 'default', show: !!record.isSupplierSendback },
  ];
  const visible = flags.filter(flag => flag.show);
  if (!visible.length) return null;
  return (
    <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: 'wrap' }}>
      {visible.map(({ label, color, title }) => (
        <Tooltip key={label} title={title ?? ''}>
          <Chip size="small" color={color === 'default' ? undefined : color} variant={color === 'default' ? 'outlined' : 'filled'} label={label} />
        </Tooltip>
      ))}
    </Stack>
  );
};
